import { Component, OnInit } from '@angular/core';
import { CdkDragEnd } from '@angular/cdk/drag-drop';
import { firstValueFrom } from 'rxjs';
import { ComputerService } from 'src/app/computer/computer.service';
import { NotificationService } from 'src/app/services/notification.service';
import {
  ConnectedWorkstationAgent,
  CreateLabelProfileDto,
  CreateWorkstationPrinterDto,
  LabelProfileLayoutJson,
  LabelProfileRef,
  TsplElementRef,
  TsplLabelRef,
  Warehouse,
  WorkstationPrinterRecord
} from 'src/typings';
import { WarehouseService } from '../warehouse.service';

type LabelLayoutKind = 'productLabel' | 'cellLabel';
type LabelLayoutZoneKey = 'article' | 'brand' | 'productName' | 'barcode' | 'cellName';
type LabelLayoutZoneType = 'text' | 'barcode';

interface LayoutZoneMeta {
  key: LabelLayoutZoneKey;
  label: string;
  type: LabelLayoutZoneType;
}

const PRINTER_MANAGER_TEMPLATE = `
<div class="printer-manager-page">
  <mat-card class="manager-hero">
    <div class="printer-manager-header">
      <div class="printer-manager-header__copy">
        <h1>Настройки принтеров</h1>
        <p>Профили этикеток, подключённые агенты и привязка принтеров к складам.</p>
      </div>
      <button mat-stroked-button type="button" (click)="loadAll()" [disabled]="loading">Обновить</button>
    </div>
  </mat-card>

  <mat-tab-group class="manager-tabs" animationDuration="0ms">
    <mat-tab label="Профили">
      <div class="tab-stack">
        <mat-card class="manager-card">
          <mat-card-header>
            <mat-card-title>Новый профиль этикеток</mat-card-title>
            <mat-card-subtitle>Шаблон параметров TSPL для конкретного размера.</mat-card-subtitle>
          </mat-card-header>
          <mat-card-content class="manager-card__content">
            <div class="field-grid create-grid">
              <mat-form-field appearance="outline">
                <mat-label>Код</mat-label>
                <input matInput [(ngModel)]="newProfile.code" />
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Название</mat-label>
                <input matInput [(ngModel)]="newProfile.name" />
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Ширина, мм</mat-label>
                <input matInput type="number" [(ngModel)]="newProfile.widthMm" />
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Высота, мм</mat-label>
                <input matInput type="number" [(ngModel)]="newProfile.heightMm" />
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Зазор, мм</mat-label>
                <input matInput type="number" [(ngModel)]="newProfile.gapMm" />
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Направление</mat-label>
                <input matInput type="number" [(ngModel)]="newProfile.direction" />
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Скорость</mat-label>
                <input matInput type="number" [(ngModel)]="newProfile.speed" />
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Плотность</mat-label>
                <input matInput type="number" [(ngModel)]="newProfile.density" />
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Кодировка</mat-label>
                <input matInput [(ngModel)]="newProfile.characterEncoding" />
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Кодовая страница</mat-label>
                <input matInput [(ngModel)]="newProfile.codePage" />
              </mat-form-field>
            </div>

            <div class="record-actions record-actions--create">
              <mat-checkbox [(ngModel)]="newProfile.isActive">Активен</mat-checkbox>
              <mat-checkbox [(ngModel)]="newProfile.isDefault">По умолчанию</mat-checkbox>
              <button mat-flat-button color="primary" type="button" (click)="createProfile()">Добавить профиль</button>
            </div>
          </mat-card-content>
        </mat-card>

        <mat-card class="manager-card">
          <mat-card-header>
            <mat-card-title>Профили этикеток</mat-card-title>
            <mat-card-subtitle>Готовые размеры и параметры печати.</mat-card-subtitle>
          </mat-card-header>

          <ng-container *ngIf="labelProfiles.length; else noProfiles">
            <mat-card-content class="manager-card__content">
              <div class="record-list">
                <section class="record-card" *ngFor="let profile of labelProfiles; trackBy: trackById">
                  <div class="record-head">
                    <div class="record-head__copy">
                      <h3>{{ getProfileTitle(profile) }}</h3>
                      <p>{{ profile.code || 'Без кода' }}</p>
                    </div>
                    <div class="record-badges">
                      <span class="status-badge" [class.status-badge--muted]="!profile.isActive">
                        {{ profile.isActive ? 'Активен' : 'Выключен' }}
                      </span>
                      <span class="status-badge status-badge--accent" *ngIf="profile.isDefault">
                        По умолчанию
                      </span>
                    </div>
                  </div>

                  <div class="field-grid">
                    <mat-form-field appearance="outline">
                      <mat-label>Код</mat-label>
                      <input matInput [(ngModel)]="profile.code" />
                    </mat-form-field>
                    <mat-form-field appearance="outline">
                      <mat-label>Название</mat-label>
                      <input matInput [(ngModel)]="profile.name" />
                    </mat-form-field>
                    <mat-form-field appearance="outline">
                      <mat-label>Ширина, мм</mat-label>
                      <input matInput type="number" [(ngModel)]="profile.widthMm" />
                    </mat-form-field>
                    <mat-form-field appearance="outline">
                      <mat-label>Высота, мм</mat-label>
                      <input matInput type="number" [(ngModel)]="profile.heightMm" />
                    </mat-form-field>
                    <mat-form-field appearance="outline">
                      <mat-label>Зазор, мм</mat-label>
                      <input matInput type="number" [(ngModel)]="profile.gapMm" />
                    </mat-form-field>
                    <mat-form-field appearance="outline">
                      <mat-label>Направление</mat-label>
                      <input matInput type="number" [(ngModel)]="profile.direction" />
                    </mat-form-field>
                    <mat-form-field appearance="outline">
                      <mat-label>Скорость</mat-label>
                      <input matInput type="number" [(ngModel)]="profile.speed" />
                    </mat-form-field>
                    <mat-form-field appearance="outline">
                      <mat-label>Плотность</mat-label>
                      <input matInput type="number" [(ngModel)]="profile.density" />
                    </mat-form-field>
                    <mat-form-field appearance="outline">
                      <mat-label>Кодировка</mat-label>
                      <input matInput [(ngModel)]="profile.characterEncoding" />
                    </mat-form-field>
                    <mat-form-field appearance="outline">
                      <mat-label>Кодовая страница</mat-label>
                      <input matInput [(ngModel)]="profile.codePage" />
                    </mat-form-field>
                  </div>

                  <div class="layout-editor" *ngIf="getActiveLayoutKind(profile) as activeKind">
                    <div class="layout-panel__header">
                      <h3>Редактор этикетки</h3>
                      <span>Перетащите зону на превью или задайте точные значения в dots.</span>
                    </div>

                    <div class="layout-toolbar">
                      <mat-button-toggle-group
                        [value]="activeKind"
                        (change)="setActiveLayoutKind(profile, $event.value)"
                        aria-label="Тип этикетки"
                      >
                        <mat-button-toggle value="productLabel">Товар</mat-button-toggle>
                        <mat-button-toggle value="cellLabel">Ячейка</mat-button-toggle>
                      </mat-button-toggle-group>
                      <button
                        mat-stroked-button
                        type="button"
                        (click)="refreshLayoutPreview(profile)"
                        [disabled]="isLayoutPreviewLoading(profile)"
                      >
                        Обновить превью
                      </button>
                    </div>

                    <div class="layout-workspace">
                      <div class="layout-preview-wrap">
                        <div
                          class="layout-preview"
                          [style.width.px]="getPreviewWidth(profile)"
                          [style.height.px]="getPreviewHeight(profile)"
                        >
                          <div
                            class="layout-preview__server-zone"
                            *ngFor="let element of getPreviewElements(profile)"
                            [class.layout-preview__server-zone--barcode]="element.type === 'barcode'"
                            [style.left.px]="getElementPreviewX(profile, element)"
                            [style.top.px]="getElementPreviewY(profile, element)"
                            [style.width.px]="getElementPreviewWidth(profile, element)"
                            [style.height.px]="getElementPreviewHeight(profile, element)"
                          >
                            {{ getElementPreviewLabel(element) }}
                          </div>

                          <button
                            class="layout-preview__zone"
                            type="button"
                            cdkDrag
                            [cdkDragBoundary]="'.layout-preview'"
                            [cdkDragFreeDragPosition]="getZoneDragPosition(profile, zone.key)"
                            (cdkDragEnded)="handleZoneDragEnded($event, profile, zone.key)"
                            (click)="selectLayoutZone(profile, zone.key)"
                            *ngFor="let zone of getLayoutZoneMetas(activeKind)"
                            [class.layout-preview__zone--selected]="isSelectedLayoutZone(profile, zone.key)"
                            [class.layout-preview__zone--barcode]="zone.type === 'barcode'"
                            [style.width.px]="getZonePreviewWidth(profile, zone.key)"
                            [style.height.px]="getZonePreviewHeight(profile, zone.key)"
                          >
                            {{ zone.label }}
                          </button>
                        </div>
                      </div>

                      <div class="layout-controls" *ngIf="getSelectedLayoutZone(profile) as zone">
                        <mat-form-field appearance="outline">
                          <mat-label>Зона</mat-label>
                          <mat-select
                            [ngModel]="getSelectedLayoutZoneKey(profile)"
                            (ngModelChange)="selectLayoutZone(profile, $event)"
                          >
                            <mat-option
                              *ngFor="let option of getLayoutZoneMetas(activeKind)"
                              [value]="option.key"
                            >
                              {{ option.label }}
                            </mat-option>
                          </mat-select>
                        </mat-form-field>

                        <div class="layout-controls__checks">
                          <mat-checkbox [(ngModel)]="zone.enabled">Включена</mat-checkbox>
                          <mat-checkbox *ngIf="isTextLayoutZone(profile)" [(ngModel)]="zone.bold">
                            Жирный
                          </mat-checkbox>
                          <mat-checkbox
                            *ngIf="isTextLayoutZone(profile)"
                            [(ngModel)]="zone.forceBitmap"
                          >
                            Bitmap
                          </mat-checkbox>
                        </div>

                        <div class="field-grid layout-grid">
                          <mat-form-field appearance="outline">
                            <mat-label>X</mat-label>
                            <input matInput type="number" [(ngModel)]="zone.x" />
                          </mat-form-field>
                          <mat-form-field appearance="outline">
                            <mat-label>Y</mat-label>
                            <input matInput type="number" [(ngModel)]="zone.y" />
                          </mat-form-field>
                          <mat-form-field appearance="outline">
                            <mat-label>Выравнивание</mat-label>
                            <mat-select [(ngModel)]="zone.align">
                              <mat-option value="left">Слева</mat-option>
                              <mat-option value="center">Центр</mat-option>
                              <mat-option value="right">Справа</mat-option>
                            </mat-select>
                          </mat-form-field>
                          <mat-form-field appearance="outline">
                            <mat-label>Ширина</mat-label>
                            <input matInput type="number" [(ngModel)]="zone.width" />
                          </mat-form-field>
                          <mat-form-field appearance="outline">
                            <mat-label>Высота</mat-label>
                            <input matInput type="number" [(ngModel)]="zone.height" />
                          </mat-form-field>
                          <mat-form-field appearance="outline" *ngIf="isTextLayoutZone(profile)">
                            <mat-label>TSPL font</mat-label>
                            <mat-select [(ngModel)]="zone.font">
                              <mat-option *ngFor="let font of tsplFonts" [value]="font">
                                {{ font }}
                              </mat-option>
                            </mat-select>
                          </mat-form-field>
                          <mat-form-field appearance="outline" *ngIf="isTextLayoutZone(profile)">
                            <mat-label>Размер bitmap</mat-label>
                            <input matInput type="number" [(ngModel)]="zone.fontSize" />
                          </mat-form-field>
                          <mat-form-field appearance="outline" *ngIf="isTextLayoutZone(profile)">
                            <mat-label>Множитель X</mat-label>
                            <input matInput type="number" [(ngModel)]="zone.xMultiplier" />
                          </mat-form-field>
                          <mat-form-field appearance="outline" *ngIf="isTextLayoutZone(profile)">
                            <mat-label>Множитель Y</mat-label>
                            <input matInput type="number" [(ngModel)]="zone.yMultiplier" />
                          </mat-form-field>
                          <mat-form-field appearance="outline" *ngIf="isTextLayoutZone(profile)">
                            <mat-label>Макс. длина</mat-label>
                            <input matInput type="number" [(ngModel)]="zone.maxLength" />
                          </mat-form-field>
                          <mat-form-field appearance="outline" *ngIf="isProductNameLayoutZone(profile)">
                            <mat-label>Длина строки</mat-label>
                            <input matInput type="number" [(ngModel)]="zone.lineLength" />
                          </mat-form-field>
                          <mat-form-field appearance="outline" *ngIf="isProductNameLayoutZone(profile)">
                            <mat-label>Строк</mat-label>
                            <input matInput type="number" [(ngModel)]="zone.maxLines" />
                          </mat-form-field>
                          <mat-form-field appearance="outline" *ngIf="isProductNameLayoutZone(profile)">
                            <mat-label>Шаг строк</mat-label>
                            <input matInput type="number" [(ngModel)]="zone.lineStep" />
                          </mat-form-field>
                          <mat-form-field appearance="outline" *ngIf="isBarcodeLayoutZone(profile)">
                            <mat-label>Тип barcode</mat-label>
                            <mat-select [(ngModel)]="zone.barcodeType">
                              <mat-option value="auto" *ngIf="activeKind === 'productLabel'">Auto</mat-option>
                              <mat-option value="128">Code128</mat-option>
                              <mat-option value="EAN13" *ngIf="activeKind === 'productLabel'">EAN13</mat-option>
                              <mat-option value="EAN8" *ngIf="activeKind === 'productLabel'">EAN8</mat-option>
                            </mat-select>
                          </mat-form-field>
                          <mat-form-field appearance="outline" *ngIf="isBarcodeLayoutZone(profile)">
                            <mat-label>Narrow</mat-label>
                            <input matInput type="number" [(ngModel)]="zone.narrow" />
                          </mat-form-field>
                          <mat-form-field appearance="outline" *ngIf="isBarcodeLayoutZone(profile)">
                            <mat-label>Wide</mat-label>
                            <input matInput type="number" [(ngModel)]="zone.wide" />
                          </mat-form-field>
                          <mat-form-field appearance="outline" *ngIf="isBarcodeLayoutZone(profile)">
                            <mat-label>Readable</mat-label>
                            <input matInput type="number" [(ngModel)]="zone.readable" />
                          </mat-form-field>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="record-actions">
                    <mat-checkbox [(ngModel)]="profile.isActive">Активен</mat-checkbox>
                    <mat-checkbox [(ngModel)]="profile.isDefault">По умолчанию</mat-checkbox>
                    <button mat-stroked-button type="button" (click)="saveProfile(profile)">Сохранить</button>
                    <button mat-button color="warn" type="button" (click)="deleteProfile(profile)">Удалить</button>
                  </div>
                </section>
              </div>
            </mat-card-content>
          </ng-container>

          <ng-template #noProfiles>
            <mat-card-content class="manager-card__content">
              <div class="empty-state">Пока нет созданных профилей этикеток.</div>
            </mat-card-content>
          </ng-template>
        </mat-card>
      </div>
    </mat-tab>

    <mat-tab label="Принтеры">
      <div class="tab-stack">
        <mat-card class="manager-card">
          <mat-card-header>
            <mat-card-title>Новый принтер склада</mat-card-title>
            <mat-card-subtitle>Привязка агента к складу, роли и профилю этикеток.</mat-card-subtitle>
          </mat-card-header>
          <mat-card-content class="manager-card__content">
            <div class="field-grid create-grid">
              <mat-form-field appearance="outline">
                <mat-label>Склад</mat-label>
                <mat-select [(ngModel)]="newPrinter.warehouseId">
                  <mat-option *ngFor="let warehouse of warehouses" [value]="warehouse.id">
                    {{ warehouse.description }}
                  </mat-option>
                </mat-select>
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Роль</mat-label>
                <mat-select [(ngModel)]="newPrinter.role" (selectionChange)="handlePrinterRoleChange(newPrinter)">
                  <mat-option value="receipt">Чеки</mat-option>
                  <mat-option value="label">Этикетки</mat-option>
                </mat-select>
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Подключённый агент</mat-label>
                <mat-select [(ngModel)]="newPrinter.deviceId">
                  <mat-option *ngFor="let agent of connectedAgents" [value]="agent.deviceId">
                    {{ getAgentLabel(agent) }}
                  </mat-option>
                </mat-select>
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Отображаемое имя</mat-label>
                <input matInput [(ngModel)]="newPrinter.displayName" />
              </mat-form-field>
              <mat-form-field appearance="outline" *ngIf="newPrinter.role === 'label'">
                <mat-label>Профиль этикетки</mat-label>
                <mat-select [(ngModel)]="newPrinter.labelProfileId">
                  <mat-option [value]="null">Авто</mat-option>
                  <mat-option *ngFor="let profile of labelProfiles" [value]="profile.id">
                    {{ profile.name || profile.code }}
                  </mat-option>
                </mat-select>
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Приоритет</mat-label>
                <input matInput type="number" [(ngModel)]="newPrinter.priority" />
              </mat-form-field>
            </div>

            <div class="record-actions record-actions--create">
              <mat-checkbox [(ngModel)]="newPrinter.isEnabled">Активен</mat-checkbox>
              <mat-checkbox [(ngModel)]="newPrinter.isDefaultForWarehouse">По умолчанию</mat-checkbox>
              <button mat-flat-button color="primary" type="button" (click)="createPrinter()">Добавить принтер</button>
            </div>
          </mat-card-content>
        </mat-card>

        <mat-card class="manager-card">
          <mat-card-header>
            <mat-card-title>Принтеры склада</mat-card-title>
            <mat-card-subtitle>Все назначенные принтеры и их статус подключения.</mat-card-subtitle>
          </mat-card-header>

          <ng-container *ngIf="printers.length; else noPrinters">
            <mat-card-content class="manager-card__content">
              <div class="record-list">
                <section class="record-card" *ngFor="let printer of printers; trackBy: trackById">
                  <div class="record-head">
                    <div class="record-head__copy">
                      <h3>{{ getPrinterTitle(printer) }}</h3>
                      <p>{{ getWarehouseLabel(printer.warehouseId) }} · {{ getRoleLabel(printer.role) }}</p>
                    </div>
                    <div class="record-badges">
                      <span class="status-badge" [class.status-badge--online]="isAgentOnline(printer.deviceId)">
                        {{ getPrinterStatusLabel(printer.deviceId) }}
                      </span>
                      <span class="status-badge status-badge--version" *ngIf="getAgentVersionByDeviceId(printer.deviceId)">
                        {{ getAgentVersionByDeviceId(printer.deviceId) }}
                      </span>
                      <span class="status-badge status-badge--muted" *ngIf="getAgentUpdateStatusByDeviceId(printer.deviceId)">
                        {{ getAgentUpdateStatusByDeviceId(printer.deviceId) }}
                      </span>
                      <span class="status-badge status-badge--accent" *ngIf="printer.isDefaultForWarehouse">
                        По умолчанию
                      </span>
                    </div>
                  </div>

                  <div class="field-grid">
                    <mat-form-field appearance="outline">
                      <mat-label>Склад</mat-label>
                      <mat-select [(ngModel)]="printer.warehouseId">
                        <mat-option *ngFor="let warehouse of warehouses" [value]="warehouse.id">
                          {{ warehouse.description }}
                        </mat-option>
                      </mat-select>
                    </mat-form-field>
                    <mat-form-field appearance="outline">
                      <mat-label>Роль</mat-label>
                      <mat-select [(ngModel)]="printer.role" (selectionChange)="handlePrinterRoleChange(printer)">
                        <mat-option value="receipt">Чеки</mat-option>
                        <mat-option value="label">Этикетки</mat-option>
                      </mat-select>
                    </mat-form-field>
                    <mat-form-field appearance="outline">
                      <mat-label>Подключённый агент</mat-label>
                      <mat-select [(ngModel)]="printer.deviceId">
                        <mat-option *ngFor="let agent of connectedAgents" [value]="agent.deviceId">
                          {{ getAgentLabel(agent) }}
                        </mat-option>
                      </mat-select>
                    </mat-form-field>
                    <mat-form-field appearance="outline">
                      <mat-label>Отображаемое имя</mat-label>
                      <input matInput [(ngModel)]="printer.displayName" />
                    </mat-form-field>
                    <mat-form-field appearance="outline" *ngIf="printer.role === 'label'">
                      <mat-label>Профиль этикетки</mat-label>
                      <mat-select [(ngModel)]="printer.labelProfileId">
                        <mat-option [value]="null">Авто</mat-option>
                        <mat-option *ngFor="let profile of labelProfiles" [value]="profile.id">
                          {{ profile.name || profile.code }}
                        </mat-option>
                      </mat-select>
                    </mat-form-field>
                    <mat-form-field appearance="outline">
                      <mat-label>Приоритет</mat-label>
                      <input matInput type="number" [(ngModel)]="printer.priority" />
                    </mat-form-field>
                  </div>

                  <div class="record-actions">
                    <mat-checkbox [(ngModel)]="printer.isEnabled">Активен</mat-checkbox>
                    <mat-checkbox [(ngModel)]="printer.isDefaultForWarehouse">По умолчанию</mat-checkbox>
                    <span class="record-hint" *ngIf="printer.role === 'label'">
                      Профиль: {{ getProfileLabel(printer.labelProfileId) }}
                    </span>
                    <button mat-stroked-button type="button" (click)="savePrinter(printer)">Сохранить</button>
                    <button mat-button color="warn" type="button" (click)="deletePrinter(printer)">Удалить</button>
                  </div>
                </section>
              </div>
            </mat-card-content>
          </ng-container>

          <ng-template #noPrinters>
            <mat-card-content class="manager-card__content">
              <div class="empty-state">Пока нет настроенных принтеров склада.</div>
            </mat-card-content>
          </ng-template>
        </mat-card>
      </div>
    </mat-tab>
  </mat-tab-group>
</div>
`;

@Component({
  selector: 'app-printer-manager',
  template: PRINTER_MANAGER_TEMPLATE,
  styleUrls: ['./printer-manager.component.scss']
})
export class PrinterManagerComponent implements OnInit {
  loading = false;
  warehouses: Warehouse[] = [];
  connectedAgents: ConnectedWorkstationAgent[] = [];
  labelProfiles: LabelProfileRef[] = [];
  printers: WorkstationPrinterRecord[] = [];
  tsplFonts = ['1', '2', '3', '4', '5'];

  newProfile: CreateLabelProfileDto = this.createEmptyProfile();
  newPrinter: CreateWorkstationPrinterDto = this.createEmptyPrinter();

  private readonly productZoneMetas: LayoutZoneMeta[] = [
    { key: 'article', label: 'Артикул', type: 'text' },
    { key: 'brand', label: 'Производитель', type: 'text' },
    { key: 'productName', label: 'Наименование', type: 'text' },
    { key: 'barcode', label: 'Штрихкод', type: 'barcode' }
  ];
  private readonly cellZoneMetas: LayoutZoneMeta[] = [
    { key: 'cellName', label: 'Ячейка', type: 'text' },
    { key: 'barcode', label: 'Штрихкод', type: 'barcode' }
  ];
  private activeLayoutKindByProfile: Record<string, LabelLayoutKind> = {};
  private selectedLayoutZoneByProfile: Record<string, LabelLayoutZoneKey> = {};
  private layoutPreviewByProfile: Record<string, TsplLabelRef | null> = {};
  private layoutPreviewLoadingByProfile: Record<string, boolean> = {};

  constructor(
    private readonly computerService: ComputerService,
    private readonly warehouseService: WarehouseService,
    private readonly notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    void this.loadAll();
  }

  async loadAll() {
    this.loading = true;
    try {
      const [warehouses, connectedAgents, labelProfiles, printers] = await Promise.all([
        firstValueFrom(this.warehouseService.getAllList()),
        firstValueFrom(this.computerService.getConnectedWorkstationAgents()),
        firstValueFrom(this.computerService.getLabelProfiles()),
        firstValueFrom(this.computerService.getWorkstationPrinters())
      ]);

      this.warehouses = warehouses;
      this.connectedAgents = connectedAgents;
      this.labelProfiles = labelProfiles.map((profile) => this.prepareProfile(profile));
      this.printers = printers;

      if (!this.newPrinter.warehouseId && warehouses[0]?.id) {
        this.newPrinter.warehouseId = warehouses[0].id;
      }
    } catch (error) {
      console.error(error);
      this.notificationService.warning('Не удалось загрузить настройки маршрутизации принтеров.');
    } finally {
      this.loading = false;
    }
  }

  async createProfile() {
    if (!String(this.newProfile.code || '').trim() || !String(this.newProfile.name || '').trim()) {
      this.notificationService.warning('Укажите код и название профиля.');
      return;
    }

    try {
      const encoding = this.normalizeEncoding(
        this.newProfile.characterEncoding,
        this.newProfile.codePage
      );
      await firstValueFrom(
        this.computerService.createLabelProfile({
          ...this.newProfile,
          characterEncoding: encoding.characterEncoding,
          codePage: encoding.codePage,
          isDefault: Boolean(this.newProfile.isDefault),
          layoutJson: this.normalizeLayoutJson(this.newProfile.layoutJson)
        })
      );
      this.newProfile = this.createEmptyProfile();
      await this.loadAll();
      this.notificationService.notice('Профиль этикеток создан.');
    } catch (error) {
      console.error(error);
      this.notificationService.warning('Не удалось создать профиль этикеток.');
    }
  }

  async saveProfile(profile: LabelProfileRef) {
    try {
      const encoding = this.normalizeEncoding(profile.characterEncoding, profile.codePage);
      await firstValueFrom(
        this.computerService.updateLabelProfile(Number(profile.id), {
          code: String(profile.code || '').trim(),
          name: String(profile.name || '').trim(),
          widthMm: Number(profile.widthMm || 0),
          heightMm: Number(profile.heightMm || 0),
          gapMm: Number(profile.gapMm || 0),
          direction: Number(profile.direction || 0),
          speed: Number(profile.speed || 0),
          density: Number(profile.density || 0),
          characterEncoding: encoding.characterEncoding,
          codePage: encoding.codePage,
          isActive: Boolean(profile.isActive),
          isDefault: Boolean(profile.isDefault),
          layoutJson: this.normalizeLayoutJson(profile.layoutJson)
        })
      );
      await this.loadAll();
      this.notificationService.notice('Профиль этикеток обновлён.');
    } catch (error) {
      console.error(error);
      this.notificationService.warning('Не удалось обновить профиль этикеток.');
    }
  }

  async deleteProfile(profile: LabelProfileRef) {
    const profileId = Number(profile.id || 0);
    if (!profileId) {
      return;
    }

    if (
      !window.confirm(`Удалить профиль этикеток "${profile.name || profile.code || profileId}"?`)
    ) {
      return;
    }

    try {
      await firstValueFrom(this.computerService.deleteLabelProfile(profileId));
      await this.loadAll();
      this.notificationService.notice('Профиль этикеток удалён.');
    } catch (error) {
      console.error(error);
      this.notificationService.warning('Не удалось удалить профиль этикеток.');
    }
  }

  async createPrinter() {
    if (
      !Number(this.newPrinter.warehouseId || 0) ||
      !String(this.newPrinter.deviceId || '').trim()
    ) {
      this.notificationService.warning('Выберите склад и подключённый агент.');
      return;
    }

    try {
      await firstValueFrom(
        this.computerService.createWorkstationPrinter({
          ...this.newPrinter,
          warehouseId: Number(this.newPrinter.warehouseId || 0),
          priority: Number(this.newPrinter.priority || 100),
          labelProfileId:
            this.newPrinter.role === 'label'
              ? Number(this.newPrinter.labelProfileId || 0) || null
              : null
        })
      );
      this.newPrinter = this.createEmptyPrinter();
      if (this.warehouses[0]?.id) {
        this.newPrinter.warehouseId = this.warehouses[0].id;
      }
      await this.loadAll();
      this.notificationService.notice('Принтер склада добавлен.');
    } catch (error) {
      console.error(error);
      this.notificationService.warning('Не удалось добавить принтер склада.');
    }
  }

  async savePrinter(printer: WorkstationPrinterRecord) {
    try {
      await firstValueFrom(
        this.computerService.updateWorkstationPrinter(printer.id, {
          deviceId: printer.deviceId,
          warehouseId: Number(printer.warehouseId || 0),
          role: printer.role,
          displayName: printer.displayName,
          isEnabled: Boolean(printer.isEnabled),
          isDefaultForWarehouse: Boolean(printer.isDefaultForWarehouse),
          priority: Number(printer.priority || 100),
          labelProfileId:
            printer.role === 'label' ? Number(printer.labelProfileId || 0) || null : null
        })
      );
      await this.loadAll();
      this.notificationService.notice('Принтер склада обновлён.');
    } catch (error) {
      console.error(error);
      this.notificationService.warning('Не удалось обновить принтер склада.');
    }
  }

  async deletePrinter(printer: WorkstationPrinterRecord) {
    if (!printer.id) {
      return;
    }

    if (!window.confirm(`Удалить принтер "${printer.displayName || printer.deviceId}"?`)) {
      return;
    }

    try {
      await firstValueFrom(this.computerService.deleteWorkstationPrinter(printer.id));
      await this.loadAll();
      this.notificationService.notice('Принтер склада удалён.');
    } catch (error) {
      console.error(error);
      this.notificationService.warning('Не удалось удалить принтер склада.');
    }
  }

  handlePrinterRoleChange(printer: WorkstationPrinterRecord | CreateWorkstationPrinterDto) {
    if (printer.role !== 'label') {
      printer.labelProfileId = null;
    }
  }

  getAgentLabel(agent: ConnectedWorkstationAgent) {
    const version = this.formatAgentVersion(agent.agentVersion);
    const versionPart = version ? `, v${version}` : '';
    return `${agent.agentName} (${agent.machineName}/${agent.userName}${versionPart})`;
  }

  getAgentVersionByDeviceId(deviceId?: string | null) {
    const agent = this.findAgentByDeviceId(deviceId);
    const version = this.formatAgentVersion(agent?.agentVersion);
    return version ? `v${version}` : '';
  }

  getAgentUpdateStatusByDeviceId(deviceId?: string | null) {
    return String(this.findAgentByDeviceId(deviceId)?.lastUpdateStatus || '').trim();
  }

  getWarehouseLabel(warehouseId?: number | null) {
    return (
      this.warehouses.find((warehouse) => warehouse.id === warehouseId)?.description ||
      'Склад не выбран'
    );
  }

  getRoleLabel(role?: string | null) {
    return role === 'receipt' ? 'Чеки' : 'Этикетки';
  }

  getProfileLabel(profileId?: number | string | null) {
    const profile = this.labelProfiles.find((item) => String(item.id) === String(profileId ?? ''));
    return profile?.name || profile?.code || 'Авто';
  }

  getPrinterTitle(printer: WorkstationPrinterRecord) {
    return (
      String(printer.displayName || '').trim() ||
      this.getAgentLabelByDeviceId(printer.deviceId) ||
      String(printer.deviceId || '').trim() ||
      'Новый принтер'
    );
  }

  getProfileTitle(profile: LabelProfileRef) {
    return (
      String(profile.name || '').trim() ||
      String(profile.code || '').trim() ||
      `Профиль ${profile.id}`
    );
  }

  getAgentLabelByDeviceId(deviceId?: string | null) {
    const agent = this.findAgentByDeviceId(deviceId);
    return agent ? this.getAgentLabel(agent) : String(deviceId || '').trim();
  }

  isAgentOnline(deviceId?: string | null) {
    return this.connectedAgents.some((agent) => agent.deviceId === deviceId);
  }

  getPrinterStatusLabel(deviceId?: string | null) {
    return this.isAgentOnline(deviceId) ? 'Онлайн' : 'Офлайн';
  }

  isWideLabelProfile(profile: { widthMm?: number | null; heightMm?: number | null }) {
    return Number(profile.widthMm || 0) >= 50 || Number(profile.heightMm || 0) >= 35;
  }

  getActiveLayoutKind(profile: LabelProfileRef): LabelLayoutKind {
    const key = this.getProfileStateKey(profile);
    return this.activeLayoutKindByProfile[key] || 'productLabel';
  }

  setActiveLayoutKind(profile: LabelProfileRef, kind: LabelLayoutKind) {
    const key = this.getProfileStateKey(profile);
    this.activeLayoutKindByProfile[key] = kind;
    const selected = this.selectedLayoutZoneByProfile[key];
    if (!this.getLayoutZoneMetas(kind).some((zone) => zone.key === selected)) {
      this.selectedLayoutZoneByProfile[key] = this.getLayoutZoneMetas(kind)[0].key;
    }
    void this.refreshLayoutPreview(profile);
  }

  getLayoutZoneMetas(kind: LabelLayoutKind): LayoutZoneMeta[] {
    return kind === 'cellLabel' ? this.cellZoneMetas : this.productZoneMetas;
  }

  getSelectedLayoutZoneKey(profile: LabelProfileRef): LabelLayoutZoneKey {
    const key = this.getProfileStateKey(profile);
    const activeKind = this.getActiveLayoutKind(profile);
    const selected = this.selectedLayoutZoneByProfile[key];
    if (selected && this.getLayoutZoneMetas(activeKind).some((zone) => zone.key === selected)) {
      return selected;
    }
    return this.getLayoutZoneMetas(activeKind)[0].key;
  }

  selectLayoutZone(profile: LabelProfileRef, zoneKey: LabelLayoutZoneKey) {
    this.selectedLayoutZoneByProfile[this.getProfileStateKey(profile)] = zoneKey;
  }

  isSelectedLayoutZone(profile: LabelProfileRef, zoneKey: LabelLayoutZoneKey) {
    return this.getSelectedLayoutZoneKey(profile) === zoneKey;
  }

  getSelectedLayoutZone(profile: LabelProfileRef): Record<string, any> | null {
    return this.getLayoutZone(
      profile,
      this.getActiveLayoutKind(profile),
      this.getSelectedLayoutZoneKey(profile)
    );
  }

  isTextLayoutZone(profile: LabelProfileRef) {
    return this.getSelectedLayoutZoneMeta(profile)?.type === 'text';
  }

  isBarcodeLayoutZone(profile: LabelProfileRef) {
    return this.getSelectedLayoutZoneMeta(profile)?.type === 'barcode';
  }

  isProductNameLayoutZone(profile: LabelProfileRef) {
    return (
      this.getActiveLayoutKind(profile) === 'productLabel' &&
      this.getSelectedLayoutZoneKey(profile) === 'productName'
    );
  }

  getPreviewWidth(profile: LabelProfileRef) {
    return Math.round(this.getLabelWidthDots(profile) * this.getPreviewScale(profile));
  }

  getPreviewHeight(profile: LabelProfileRef) {
    return Math.round(this.getLabelHeightDots(profile) * this.getPreviewScale(profile));
  }

  getZoneDragPosition(profile: LabelProfileRef, zoneKey: LabelLayoutZoneKey) {
    const activeKind = this.getActiveLayoutKind(profile);
    const zone = this.getLayoutZone(profile, activeKind, zoneKey);
    const scale = this.getPreviewScale(profile);
    const x = this.resolveZoneX(profile, activeKind, zoneKey);
    const y = Number(zone?.y || 0);

    return {
      x: Math.round(x * scale),
      y: Math.round(Math.max(0, y) * scale)
    };
  }

  getZonePreviewWidth(profile: LabelProfileRef, zoneKey: LabelLayoutZoneKey) {
    const activeKind = this.getActiveLayoutKind(profile);
    const zone = this.getLayoutZone(profile, activeKind, zoneKey);
    const scale = this.getPreviewScale(profile);
    const fallback = this.getLayoutZoneMeta(activeKind, zoneKey)?.type === 'barcode' ? 150 : 120;
    return Math.max(32, Math.round(Number(zone?.width || fallback) * scale));
  }

  getZonePreviewHeight(profile: LabelProfileRef, zoneKey: LabelLayoutZoneKey) {
    const activeKind = this.getActiveLayoutKind(profile);
    const zone = this.getLayoutZone(profile, activeKind, zoneKey);
    const scale = this.getPreviewScale(profile);
    const fallback = this.getLayoutZoneMeta(activeKind, zoneKey)?.type === 'barcode' ? 54 : 28;
    return Math.max(18, Math.round(Number(zone?.height || fallback) * scale));
  }

  handleZoneDragEnded(event: CdkDragEnd, profile: LabelProfileRef, zoneKey: LabelLayoutZoneKey) {
    const zone = this.getLayoutZone(profile, this.getActiveLayoutKind(profile), zoneKey);
    if (!zone) {
      return;
    }

    const position = event.source.getFreeDragPosition();
    const scale = this.getPreviewScale(profile);
    zone.x = Math.max(0, Math.round(position.x / scale));
    zone.y = Math.max(0, Math.round(position.y / scale));
    this.selectLayoutZone(profile, zoneKey);
    void this.refreshLayoutPreview(profile);
  }

  getPreviewElements(profile: LabelProfileRef): TsplElementRef[] {
    return this.layoutPreviewByProfile[this.getProfileStateKey(profile)]?.elements || [];
  }

  getElementPreviewX(profile: LabelProfileRef, element: TsplElementRef) {
    return Math.round(Number(element.x || 0) * this.getPreviewScale(profile));
  }

  getElementPreviewY(profile: LabelProfileRef, element: TsplElementRef) {
    return Math.round(Number(element.y || 0) * this.getPreviewScale(profile));
  }

  getElementPreviewWidth(profile: LabelProfileRef, element: TsplElementRef) {
    const scale = this.getPreviewScale(profile);
    if (element.type === 'barcode') {
      return Math.max(40, Math.round(this.estimatePreviewBarcodeWidth(element) * scale));
    }
    return Math.max(
      24,
      Math.round(Number(element.width || this.estimatePreviewTextWidth(element)) * scale)
    );
  }

  getElementPreviewHeight(profile: LabelProfileRef, element: TsplElementRef) {
    const scale = this.getPreviewScale(profile);
    const fallback = element.type === 'barcode' ? 52 : element.type === 'text-bitmap' ? 28 : 24;
    return Math.max(14, Math.round(Number(element.height || fallback) * scale));
  }

  getElementPreviewLabel(element: TsplElementRef) {
    return element.type === 'barcode' ? 'BARCODE' : element.text || element.content || element.type;
  }

  isLayoutPreviewLoading(profile: LabelProfileRef) {
    return Boolean(this.layoutPreviewLoadingByProfile[this.getProfileStateKey(profile)]);
  }

  async refreshLayoutPreview(profile: LabelProfileRef) {
    const key = this.getProfileStateKey(profile);
    this.layoutPreviewLoadingByProfile[key] = true;
    try {
      const request =
        this.getActiveLayoutKind(profile) === 'cellLabel'
          ? this.computerService.previewCellLabelProfile(profile)
          : this.computerService.previewProductLabelProfile(profile);
      this.layoutPreviewByProfile[key] = await firstValueFrom(request);
    } catch (error) {
      console.error(error);
      this.notificationService.warning('Не удалось обновить превью этикетки.');
    } finally {
      this.layoutPreviewLoadingByProfile[key] = false;
    }
  }

  trackById(_index: number, item: { id?: number | string }) {
    return item.id;
  }

  private prepareProfile(profile: LabelProfileRef): LabelProfileRef {
    return {
      ...profile,
      layoutJson: this.normalizeLayoutJson(profile.layoutJson)
    };
  }

  private getProfileStateKey(profile: LabelProfileRef) {
    return String(profile.id || profile.code || 'new');
  }

  private getSelectedLayoutZoneMeta(profile: LabelProfileRef): LayoutZoneMeta | undefined {
    return this.getLayoutZoneMeta(
      this.getActiveLayoutKind(profile),
      this.getSelectedLayoutZoneKey(profile)
    );
  }

  private getLayoutZoneMeta(
    kind: LabelLayoutKind,
    zoneKey: LabelLayoutZoneKey
  ): LayoutZoneMeta | undefined {
    return this.getLayoutZoneMetas(kind).find((zone) => zone.key === zoneKey);
  }

  private getLayoutZone(
    profile: LabelProfileRef,
    kind: LabelLayoutKind,
    zoneKey: LabelLayoutZoneKey
  ): Record<string, any> | null {
    const layoutJson = profile.layoutJson as any;
    return this.isObject(layoutJson?.[kind]?.[zoneKey]) ? layoutJson[kind][zoneKey] : null;
  }

  private getLabelWidthDots(profile: LabelProfileRef) {
    return Math.max(1, Math.round(Number(profile.widthMm || 0) * 8));
  }

  private getLabelHeightDots(profile: LabelProfileRef) {
    return Math.max(1, Math.round(Number(profile.heightMm || 0) * 8));
  }

  private getPreviewScale(profile: LabelProfileRef) {
    const widthDots = this.getLabelWidthDots(profile);
    const heightDots = this.getLabelHeightDots(profile);
    return Math.min(1, 360 / widthDots, 240 / heightDots);
  }

  private resolveZoneX(
    profile: LabelProfileRef,
    kind: LabelLayoutKind,
    zoneKey: LabelLayoutZoneKey
  ) {
    const zone = this.getLayoutZone(profile, kind, zoneKey);
    if (!zone) {
      return 0;
    }

    if (zone.x !== undefined && zone.x !== null && Number.isFinite(Number(zone.x))) {
      return Math.max(0, Number(zone.x));
    }

    const widthDots = this.getLabelWidthDots(profile);
    const margins = this.getLayoutMargins(profile, kind);
    const zoneWidth = Number(
      zone.width || (this.getLayoutZoneMeta(kind, zoneKey)?.type === 'barcode' ? 150 : 120)
    );
    const align = String(zone.align || 'center');

    if (align === 'left') {
      return margins.left;
    }
    if (align === 'right') {
      return Math.max(0, widthDots - margins.right - zoneWidth);
    }

    return Math.max(margins.left, Math.round((widthDots - zoneWidth) / 2));
  }

  private getLayoutMargins(profile: LabelProfileRef, kind: LabelLayoutKind) {
    const margins = (profile.layoutJson as any)?.[kind]?.margins || {};
    return {
      left: Number(margins.left || 0),
      right: Number(margins.right || 0)
    };
  }

  private estimatePreviewTextWidth(element: TsplElementRef) {
    const text = String(element.text || '');
    const fontSize = Number(element.fontSize || 16);
    const multiplier = Number(element.xMultiplier || 1);
    return Math.max(24, text.length * fontSize * 0.65 * multiplier);
  }

  private estimatePreviewBarcodeWidth(element: TsplElementRef) {
    const content = String(element.content || '');
    const narrow = Math.max(1, Number(element.narrow || 2));
    return Math.max(80, (48 + content.length * 11) * narrow);
  }

  private createEmptyProfile(): CreateLabelProfileDto {
    return {
      code: '',
      name: '',
      widthMm: 30,
      heightMm: 20,
      gapMm: 2,
      direction: 0,
      speed: 2,
      density: 8,
      characterEncoding: 'windows-1251',
      codePage: '1251',
      isActive: true,
      isDefault: false,
      layoutJson: this.createDefaultLayoutJson()
    };
  }

  private createEmptyPrinter(): CreateWorkstationPrinterDto {
    return {
      deviceId: '',
      warehouseId: 0,
      role: 'label',
      displayName: '',
      isEnabled: true,
      isDefaultForWarehouse: false,
      priority: 100,
      labelProfileId: null
    };
  }

  private findAgentByDeviceId(deviceId?: string | null) {
    return this.connectedAgents.find((item) => item.deviceId === deviceId);
  }

  private formatAgentVersion(version?: string | null) {
    const trimmed = String(version || '').trim();
    if (!trimmed) {
      return '';
    }

    const [main, metadata] = trimmed.split('+', 2);
    if (!metadata) {
      return main;
    }

    return `${main}+${metadata.split('.', 1)[0]}`;
  }

  private normalizeEncoding(characterEncoding?: string | null, codePage?: string | null) {
    const normalizedEncoding = String(characterEncoding || '').trim() || 'windows-1251';
    const normalizedCodePage = String(codePage || '').trim();

    if (/1251/i.test(normalizedEncoding)) {
      return {
        characterEncoding: normalizedEncoding,
        codePage: normalizedCodePage || '1251'
      };
    }

    return {
      characterEncoding: normalizedEncoding,
      codePage: normalizedCodePage || null
    };
  }

  private normalizeLayoutJson(
    layoutJson?: Partial<LabelProfileLayoutJson> | null
  ): LabelProfileLayoutJson {
    const defaults = this.createDefaultLayoutJson();
    const source: Record<string, any> = this.isObject(layoutJson) ? layoutJson : {};
    const productLabel: Record<string, any> = this.isObject(source.productLabel)
      ? source.productLabel
      : {};
    const cellLabel: Record<string, any> = this.isObject(source.cellLabel) ? source.cellLabel : {};

    return {
      ...(source as any),
      schemaVersion: Number(source.schemaVersion || defaults.schemaVersion || 1),
      units: 'dots',
      productLabel: {
        margins: {
          ...defaults.productLabel.margins,
          ...(this.isObject(productLabel.margins) ? productLabel.margins : {})
        },
        article: {
          ...defaults.productLabel.article,
          ...(this.isObject(productLabel.article) ? productLabel.article : {})
        },
        brand: {
          ...defaults.productLabel.brand,
          ...(this.isObject(productLabel.brand) ? productLabel.brand : {})
        },
        productName: {
          ...defaults.productLabel.productName,
          ...(this.isObject(productLabel.productName) ? productLabel.productName : {})
        },
        barcode: {
          ...defaults.productLabel.barcode,
          ...(this.isObject(productLabel.barcode) ? productLabel.barcode : {})
        }
      },
      cellLabel: {
        margins: {
          ...defaults.cellLabel.margins,
          ...(this.isObject(cellLabel.margins) ? cellLabel.margins : {})
        },
        cellName: {
          ...defaults.cellLabel.cellName,
          ...(this.isObject(cellLabel.cellName) ? cellLabel.cellName : {})
        },
        barcode: {
          ...defaults.cellLabel.barcode,
          ...(this.isObject(cellLabel.barcode) ? cellLabel.barcode : {})
        }
      }
    };
  }

  private createDefaultLayoutJson(): LabelProfileLayoutJson {
    return {
      schemaVersion: 1,
      units: 'dots',
      productLabel: {
        margins: {
          left: 18,
          right: 18,
          top: 14
        },
        article: {
          enabled: true,
          x: null,
          y: 14,
          align: 'center',
          font: '3',
          xMultiplier: 1,
          yMultiplier: 1,
          maxLength: 30,
          trimLength: 34,
          height: 24,
          gapAfter: 14
        },
        brand: {
          enabled: true,
          x: null,
          y: 52,
          align: 'center',
          font: '3',
          xMultiplier: 1,
          yMultiplier: 1,
          maxLength: 30,
          trimLength: 34,
          height: 24,
          bitmapHeight: 24,
          bitmapFontSize: 20,
          bold: true,
          gapAfter: 8
        },
        productName: {
          enabled: true,
          x: null,
          y: 84,
          align: 'center',
          forceBitmap: true,
          width: 432,
          height: 32,
          fontSize: 26,
          bold: true,
          maxLength: 92,
          lineLength: 24,
          maxLines: 2,
          lineStep: 34,
          font: '3'
        },
        barcode: {
          enabled: true,
          x: null,
          y: 228,
          align: 'center',
          barcodeType: 'auto',
          height: 64,
          narrow: 3,
          wide: 6,
          readable: 0,
          maxNarrow: 4,
          gapAfterText: 12,
          bottomReserve: 92,
          bottomPadding: 12,
          minHeight: 56,
          maxHeight: 78
        }
      },
      cellLabel: {
        margins: {
          left: 12,
          right: 12,
          top: 16
        },
        cellName: {
          enabled: true,
          x: null,
          y: 16,
          align: 'center',
          font: '4',
          xMultiplier: 2,
          yMultiplier: 2,
          maxLength: 18
        },
        barcode: {
          enabled: true,
          x: null,
          y: 230,
          align: 'center',
          barcodeType: '128',
          height: 52,
          narrow: 2,
          wide: 2,
          readable: 0,
          maxNarrow: 3,
          width: 440
        }
      }
    };
  }

  private isObject(value: unknown): value is Record<string, any> {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
  }
}
